import { createColumnHelper } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import type { DeskTableFeatures } from "@/modules/shell/data-table";
import type { PayMethod } from "@/shared/api/pay-methods";

const columnHelper = createColumnHelper<DeskTableFeatures, PayMethod>();

export type PayMethodAssetAccount = {
  id: string;
  code: string;
  name: string;
};

export type PayMethodColumnsOpts = {
  busy: boolean;
  assetAccounts: PayMethodAssetAccount[];
  accountLabel: (code: string) => string;
  onRename: (row: PayMethod, nextName: string) => void;
  onSetGl: (row: PayMethod, code: string) => void;
  onSetDefault: (row: PayMethod) => void;
  onToggleOffline: (row: PayMethod) => void;
  onDeactivate: (row: PayMethod) => void;
  onReactivate: (row: PayMethod) => void;
};

export function payMethodColumns(opts: PayMethodColumnsOpts) {
  return columnHelper.columns([
    columnHelper.accessor("name", {
      id: "name",
      header: "Name",
      size: 180,
      cell: ({ row }) => {
        const m = row.original;
        return (
          <Input
            className="font-medium"
            key={`${m.id}-${m.name}`}
            defaultValue={m.name}
            disabled={opts.busy || m.deactivatedAt != null}
            onBlur={(e) => opts.onRename(m, e.target.value)}
          />
        );
      },
    }),
    columnHelper.accessor("code", {
      id: "code",
      header: "Code",
      size: 88,
      cell: (info) => (
        <span className="font-mono text-sm">{info.getValue()}</span>
      ),
    }),
    columnHelper.display({
      id: "status",
      header: "Status",
      size: 160,
      enableSorting: false,
      cell: ({ row }) => {
        const m = row.original;
        return (
          <div className="flex flex-wrap items-center gap-1.5">
            {m.isDefault ? <Badge variant="primary">default</Badge> : null}
            {m.worksOffline ? (
              <Badge variant="success">offline</Badge>
            ) : (
              <Badge variant="outline">online only</Badge>
            )}
            {m.deactivatedAt ? (
              <Badge variant="outline">deactivated</Badge>
            ) : null}
          </div>
        );
      },
    }),
    columnHelper.accessor("glAccountCode", {
      id: "gl",
      header: "GL",
      size: 200,
      cell: ({ row }) => {
        const m = row.original;
        if (m.deactivatedAt == null && opts.assetAccounts.length > 0) {
          return (
            <div>
              <Label className="text-xs">GL account</Label>
              <Select
                className="mt-0.5 max-w-xs"
                value={m.glAccountCode}
                disabled={opts.busy}
                onChange={(e) => opts.onSetGl(m, e.target.value)}
              >
                {!opts.assetAccounts.some(
                  (a) => a.code === m.glAccountCode,
                ) && (
                  <option value={m.glAccountCode}>
                    {opts.accountLabel(m.glAccountCode)}
                  </option>
                )}
                {opts.assetAccounts.map((a) => (
                  <option key={a.id} value={a.code}>
                    {a.code} · {a.name}
                  </option>
                ))}
              </Select>
            </div>
          );
        }
        return (
          <span className="text-xs text-[var(--color-muted)]">
            GL {opts.accountLabel(m.glAccountCode)}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      size: 260,
      enableSorting: false,
      enableResizing: false,
      cell: ({ row }) => {
        const m = row.original;
        return (
          <div className="flex flex-wrap justify-end gap-1">
            {!m.isDefault && m.deactivatedAt == null ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={opts.busy}
                onClick={() => opts.onSetDefault(m)}
              >
                Set default
              </Button>
            ) : null}
            {m.deactivatedAt == null ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={opts.busy}
                onClick={() => opts.onToggleOffline(m)}
              >
                {m.worksOffline ? "Online only" : "Allow offline"}
              </Button>
            ) : null}
            {m.deactivatedAt == null && !m.isDefault ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={opts.busy}
                onClick={() => opts.onDeactivate(m)}
              >
                Deactivate
              </Button>
            ) : null}
            {m.deactivatedAt != null ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={opts.busy}
                onClick={() => opts.onReactivate(m)}
              >
                Reactivate
              </Button>
            ) : null}
          </div>
        );
      },
    }),
  ]);
}
